/**
 * 通用图片上传表单项，支持单图/多图；上传逻辑由父组件 customRequest 注入。
 */
import { DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import { Button, Image, Space, Upload } from 'antd';
import type { UploadProps } from 'antd';
import React, { useCallback } from 'react';
import {
  compressImageFileIfNeeded,
  mergeImageUploadHint,
  type ImageUploadHintVariant,
} from '@/utils/imageUpload';

type SharedImageUploadFieldProps = {
  accept?: string;
  customRequest: NonNullable<UploadProps['customRequest']>;
  hint?: string;
  hintVariant?: ImageUploadHintVariant;
  previewHeight?: number;
  previewWidth?: number;
  uploading?: boolean;
  uploadingText: string;
  uploadText: string;
};

type SingleImageUploadFieldProps = SharedImageUploadFieldProps & {
  compactMode?: boolean;
  multiple?: false;
  onChange: (next?: string) => void;
  removeLabel?: string;
  value?: string;
};

type MultiImageUploadFieldProps = SharedImageUploadFieldProps & {
  limit: number;
  multiple: true;
  onChange: (next: string[]) => void;
  value?: string[];
};

type ImageUploadFieldProps = SingleImageUploadFieldProps | MultiImageUploadFieldProps;

const ImageUploadField: React.FC<ImageUploadFieldProps> = (props) => {
  const multiple = props.multiple === true;
  const imageUrls = multiple ? (props.value || []).filter(Boolean) : props.value ? [props.value] : [];
  const uploadDisabled = props.uploading || (multiple && imageUrls.length >= props.limit);
  const previewWidth = props.previewWidth ?? (multiple ? 128 : 220);
  const previewHeight = props.previewHeight ?? (multiple ? 80 : 124);
  const mergedHint = mergeImageUploadHint(props.hint, props.hintVariant ?? 'content');

  const wrappedCustomRequest = useCallback<NonNullable<UploadProps['customRequest']>>(
    async (options, info) => {
      const rawFile = options.file as File;
      try {
        const preparedFile = await compressImageFileIfNeeded(rawFile);
        props.customRequest({ ...options, file: preparedFile }, info);
      } catch (error) {
        options.onError?.(error as Error);
      }
    },
    [props.customRequest],
  );

  const handleRemove = (index: number) => {
    if (multiple) {
      props.onChange(imageUrls.filter((_, itemIndex) => itemIndex !== index));
      return;
    }
    props.onChange(undefined);
  };

  const compactMode = !multiple && props.compactMode;
  const hideDragger = (compactMode && imageUrls.length > 0) || (multiple && imageUrls.length >= props.limit);

  return (
    <Space direction='vertical' size={multiple ? 12 : 8} style={{ width: '100%' }}>
      {multiple ? (
        imageUrls.length ? (
          <Space wrap size={12}>
            {imageUrls.map((imageUrl, index) => (
              <div key={`${imageUrl}-${index}`} style={{ position: 'relative', width: previewWidth, height: previewHeight, display: 'inline-block' }}>
                <Image
                  src={imageUrl}
                  width={previewWidth}
                  height={previewHeight}
                  style={{ objectFit: 'cover', borderRadius: 6 }}
                />
                <Button
                  type='primary'
                  danger
                  shape='circle'
                  icon={<DeleteOutlined />}
                  size='small'
                  style={{ position: 'absolute', top: -8, right: -8, zIndex: 10 }}
                  onClick={() => handleRemove(index)}
                />
              </div>
            ))}
          </Space>
        ) : null
      ) : imageUrls[0] ? (
        <div style={{ position: 'relative', width: previewWidth, height: previewHeight, display: 'inline-block' }}>
          <Image
            src={imageUrls[0]}
            width={previewWidth}
            height={previewHeight}
            style={{ objectFit: 'cover', borderRadius: 6 }}
          />
          {compactMode ? (
            <Button
              type='primary'
              danger
              shape='circle'
              icon={<DeleteOutlined />}
              size='small'
              style={{ position: 'absolute', top: -8, right: -8, zIndex: 10 }}
              onClick={() => handleRemove(0)}
            />
          ) : null}
        </div>
      ) : null}
      {!hideDragger && (
        <Upload.Dragger
          accept={props.accept || 'image/*'}
          multiple={multiple}
          showUploadList={false}
          customRequest={wrappedCustomRequest}
          disabled={uploadDisabled}
        >
          <p className='ant-upload-drag-icon'>
            <InboxOutlined />
          </p>
          <p className='ant-upload-text'>{props.uploading ? props.uploadingText : props.uploadText}</p>
          <p className='ant-upload-hint'>{mergedHint}</p>
        </Upload.Dragger>
      )}
      {!multiple && !compactMode && imageUrls[0] ? (
        <Button icon={<DeleteOutlined />} onClick={() => handleRemove(0)}>
          {props.removeLabel || '移除图片'}
        </Button>
      ) : null}
    </Space>
  );
};

export default ImageUploadField;
